import type { ReactNode } from 'react'
import { FileText, LogIn, LogOut, Moon, Sun, ShieldCheck } from 'lucide-react'
import { useQueryClient } from '@tanstack/react-query'
import { useNavigate } from '@tanstack/react-router'

import { PloteaSymbol, PloteaWordmark } from '@/components/Logo'
import { Avatar, AvatarFallback } from '@/components/ui/avatar'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { SidebarTrigger } from '@/components/ui/sidebar'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { logout } from '@/lib/endpoints/auth'
import { ploteaApp } from '@/lib/productProfile'
import { qk } from '@/lib/queryKeys'
import { useTheme } from '@/lib/theme'
import { useOrgSwitch } from '@/components/OrgSwitcher'
import { OrgCrumb, useAccountOrgMenu } from './AccountOrgMenu'

/**
 * Site's top bar (Site is the Plotea product in both builds): symbol and
 * wordmark, breadcrumbs, search, document history, theme and the account.
 * It replaces the app shell header on the Site map, so the shell's sidebar
 * trigger moves here (`inShell`).
 */
export function SiteTopBar({
  breadcrumbs,
  search,
  history,
  userName,
  inShell = false,
}: {
  breadcrumbs: ReactNode
  search: ReactNode
  history?: ReactNode
  /** Signed-in user; absent in the standalone development entry. */
  userName?: string
  inShell?: boolean
}) {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const theme = useTheme()
  const signOut = () => {
    void logout().finally(() => {
      queryClient.removeQueries({ queryKey: qk.me() })
      void navigate({ to: '/login', replace: true })
    })
  }
  const themeLabel = theme.dark ? 'Jasny motyw' : 'Ciemny motyw'
  return (
    <header className="site-top-bar">
      {inShell && <SidebarTrigger />}
      <a href="/site" className="site-top-bar-brand" aria-label="Plotea">
        <PloteaSymbol className="site-top-bar-symbol" aria-hidden />
        <PloteaWordmark className="site-top-bar-wordmark" aria-hidden />
      </a>
      <nav aria-label="Ścieżka" className="site-top-bar-crumbs">
        {userName && <ActiveOrgCrumb />}
        {breadcrumbs}
      </nav>
      <div className="site-top-bar-search">{search}</div>
      <div className="site-top-bar-actions">
        {history}
        <Tooltip>
          <TooltipTrigger
            render={
              <Button
                variant="ghost"
                size="icon-sm"
                aria-label={themeLabel}
                onClick={theme.toggle}
              />
            }
          >
            {theme.dark ? <Sun /> : <Moon />}
          </TooltipTrigger>
          <TooltipContent>{themeLabel}</TooltipContent>
        </Tooltip>
        {userName ? (
          <AccountMenu userName={userName} onSignOut={signOut} />
        ) : (
          <Button
            variant="outline"
            size="sm"
            onClick={() => void navigate({ to: '/login' })}
          >
            <LogIn />
            Zaloguj
          </Button>
        )}
      </div>
    </header>
  )
}

/** The signed-in account: who, which organization, legal documents, and leaving. */
function AccountMenu({
  userName,
  onSignOut,
}: {
  userName: string
  onSignOut: () => void
}) {
  const org = useAccountOrgMenu()
  const navigate = useNavigate()
  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger
          render={
            <Button
              variant="ghost"
              size="icon"
              aria-label={`Konto: ${userName}`}
            />
          }
        >
          <Avatar className="size-7">
            <AvatarFallback>
              {(userName.charAt(0) || 'U').toUpperCase()}
            </AvatarFallback>
          </Avatar>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" sideOffset={6}>
          <DropdownMenuGroup>
            <DropdownMenuLabel>{userName}</DropdownMenuLabel>
          </DropdownMenuGroup>
          {org.items}
          {ploteaApp && (
            <>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onClick={() => void navigate({ to: '/privacy' })}
              >
                <ShieldCheck />
                Polityka prywatności
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => void navigate({ to: '/terms' })}>
                <FileText />
                Regulamin
              </DropdownMenuItem>
            </>
          )}
          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={onSignOut}>
            <LogOut />
            Wyloguj
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
      {org.dialog}
    </>
  )
}

/** Signed in to an organization: its name leads the breadcrumbs. */
function ActiveOrgCrumb() {
  const { label, activeOrg } = useOrgSwitch()
  return activeOrg ? <OrgCrumb label={label} /> : null
}
